import { computed, Ref, ref, watch } from "vue";
import preset from "jss-preset-default";
import { injectJssContext } from "@fresh8/vue-jss/src";
import { Font, NormalizedEntity } from "@fresh8/types/lib/index";
import { StyleSheet, create } from "jss";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

type Fonts = NormalizedEntity<Font>;

let setup = false;
let fontSheet: StyleSheet<"@font-face"> | null = null;

let fontsRef: Ref<Record<string, Font>> | null = null;

export function installFonts(): void {
  const brand = injectBrand();

  const fonts = computed<NormalizedEntity<Font>>(() => {
    return {
      ...brand?.fonts.value,
      // Add the default font, we use this as a fallback font if all fonts are removed
      default: {
        name: "Default",
        color: "",
        fontAsset: {
          url: "//cdn.fresh8.co/assets/fonts/Roboto/Roboto-Regular.woff2",
          name: "Regular",
          family: "Roboto",
          fontFamily: "RegularRoboto",
          sizeScalar: 1,
          lineHeightScalar: 1
        }
      }
    };
  });

  const jssContext = injectJssContext();
  let { jss } = jssContext;
  const rootNode = jssContext.insertionPoint?.getRootNode();

  // only run setup once per whole app lifecycle
  if (!setup) {
    // Check if in shadowDom, create a new jss context if so.
    if (rootNode && (rootNode as ShadowRoot).host) {
      jss = create(preset());
    }

    // init font ref
    fontsRef = ref({});

    // this watcher should only run once per frame at most
    watch(
      () => ({ fonts: Object.values(fontsRef?.value || {}) }),
      ({ fonts }) => {
        const fontFaces = Object.keys(fonts).map(key => {
          const font = fonts[key];

          return {
            fontFamily: font.fontAsset.fontFamily,
            src: `url('${font.fontAsset.url}')`
          };
        });

        if (fontSheet) {
          fontSheet.detach();
        }

        fontSheet =
          jss.createStyleSheet({
            "@font-face": (fontFaces as unknown) as string
          }) || null;

        fontSheet?.attach();
      }
    );

    setup = true;
  }

  // fontsRef is shared across all instances so any fonts in payload provided
  // to any instance are watched and collated into fontsRef
  function patchFonts(updatedFonts: Fonts) {
    if (updatedFonts) {
      Object.keys(updatedFonts).forEach(key => {
        const font = updatedFonts[key];
        if (
          fontsRef &&
          fontsRef.value &&
          font.fontAsset &&
          !fontsRef.value[font.fontAsset.fontFamily]
        ) {
          fontsRef.value = {
            ...fontsRef.value,
            [font.fontAsset?.fontFamily]: font
          };
        }
      });
    }
  }

  if (fonts?.value) {
    patchFonts(fonts.value);
  }

  watch(
    () => fonts?.value,
    () => {
      if (fonts?.value) patchFonts(fonts.value);
    }
  );
}
